import { StateCreator } from 'zustand'
import type { CompanyStore } from '../types'
import type { Company, TeamMember, User } from '../../types/ffe'
import { colors } from '../../theme/colors'
import { detectUserTaxSystem } from '../../utils/tax/detection'
import { getCountriesForTimezone } from 'countries-and-timezones'

export interface CompanySlice {
  setCompany: (company: Company) => void
  patchCompany: (company: Partial<Company>) => void
  updateSettings: (settings: Company['settings']) => void
  patchSettings: (settings: Partial<Company['settings']>) => void
  updateUser: (user: User | null) => void
  setTeamMembers: (teamMembers: Array<TeamMember>) => void
  updateTeamMember: (teamMember: TeamMember) => void
  addTeamMember: (teamMember: TeamMember) => void
}

const tax = detectUserTaxSystem()

export const initialCompany: Company = {
  _id: undefined,
  name: '',
  address: '',
  phone: '',
  email: '',
  logo: '',
  country: getCountriesForTimezone(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )[0]?.id ?? '',

  settings: {
    taxNumber: '',
    defaultMargin: 30,
    taxRate: tax.defaultRate,
    primaryColor: colors.primary
  },
  projectSettings: {
    defaultHeader: '<p><strong>Client: </strong> </p><p><strong>Date: </strong>{today}</p><p><strong>Issue: </strong>Concept Design</p>',
    defaultFooter: ''
  }
}

export const createCompanySlice: StateCreator<
  CompanyStore,
  [],
  [],
  CompanySlice
> = (set) => ({
  setCompany: (company) => set({ company }),

  patchCompany: (partialCompany) =>
    set((state) => ({
      company: { ...state.company, ...partialCompany }
    })),

  updateUser: (user) =>
    set(() => ({
      user: user
    })),

  patchSettings: (settings) => {
    set((state) => {
      return {
        company: {
          ...state.company,
          settings: { ...state.company.settings, ...settings }
        }
      }
    })
  },

  setTeamMembers: (teamMembers: Array<TeamMember>) => {
    set(() => ({ teamMembers }))
  },

  updateTeamMember: (teamMember: TeamMember) => {
    set((state) => {
      return {
        teamMembers: state.teamMembers.map((tm) =>
          tm._id === teamMember._id ? teamMember : tm
        )
      }
    })
  },

  updateSettings: (settings) => {
    set((state) => {
      return {
        company: {
          ...state.company,
          settings: { ...state.company.settings, settings }
        }
      }
    })
  },

  addTeamMember: (teamMember) => {
    set((state) => {
      return {
        teamMembers: [...state.teamMembers, teamMember]
      }
    })
  }
})
